import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./ComplaintsStatusesChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import Notifications330Service from "../Notifications330Service";
import { Notifications330FilterModel } from "../Notifications330FilterModel";
import { ComplaintsStatusesChartReadModel } from "../Notifications330ReadModel";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
} from "../../chart/monitorDashboardChartKit";
import { saveNotifications330FilterToStorage, NOTIFICATIONS330_DETAILS_ROUTES, buildNotifications330ScopePayload } from "../notifications330DetailsShared";

@WithRender
@Component({
    diProvide: [Notifications330Service],
})
export default class ComplaintsStatusesChart extends Vue {
    @Inject(Notifications330Service)
    public notifications330Service!: Notifications330Service;

    @Prop({ required: true }) public filter!: Notifications330FilterModel;

    private chart: any = null;
    private canvasId: string = "complaints-statuses-chart-canvas";

    public hasData: boolean = false;
    public isLoading: boolean = false;
    public totalCount: number = 0;
    private requestVersion: number = 0;

    private get statusAxisOrder(): number[] {
        return [1, 3, 2];
    }

    private statusLabel(statusId: number): string {
        if (statusId === 1) {
            return String(this.$t("monitorDashboard.statusPendingForReview"));
        }
        if (statusId === 2) {
            return String(this.$t("monitorDashboard.statusDeactivated"));
        }
        if (statusId === 3) {
            return String(this.$t("monitorDashboard.statusFalseComplaint"));
        }
        return "";
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const payload = buildNotifications330ScopePayload(this.filter);

        const resp = await this.notifications330Service.GetComplaintsStatuses(payload);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;

        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.totalCount = 0;
            this.destroyChart();
            return;
        }

        this.totalCount = resp.totalCount ? Number(resp.totalCount) : 0;
        this.hasData = this.totalCount > 0;

        await this.$nextTick();

        if (!this.hasData) {
            this.destroyChart();
            return;
        }

        this.renderChart(resp.result as ComplaintsStatusesChartReadModel[]);
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(statusId: number | null): void {
        if (!this.filter) {
            return;
        }

        if (statusId != null && statusId > 0) {
            saveNotifications330FilterToStorage(
                this.filter,
                undefined,
                undefined,
                undefined,
                statusId,
            );
        } else {
            saveNotifications330FilterToStorage(this.filter);
        }

        this.$router.push({ name: NOTIFICATIONS330_DETAILS_ROUTES.statuses });
    }

    public beforeDestroy(): void {
        this.destroyChart();
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private renderChart(rows: ComplaintsStatusesChartReadModel[]): void {
        const map: { [key: string]: number } = {};
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            map[String(r.status)] = Number(r.count);
        }

        const order = this.statusAxisOrder;
        const labels: string[] = [];
        const valuesPerStatus: number[] = [];
        for (let i = 0; i < order.length; i++) {
            const id = order[i];
            labels.push(this.statusLabel(id));
            const v = map.hasOwnProperty(String(id)) ? Number(map[String(id)]) : 0;
            valuesPerStatus.push(v);
        }

        const datasets: any[] = [];
        for (let i = 0; i < order.length; i++) {
            const fill = getMonitorDashboardChartColor("complaintsStatuses", String(order[i]));
            const border = monitorDashboardDarkerBorder(fill);
            const data: any[] = new Array(order.length);
            for (let j = 0; j < order.length; j++) {
                data[j] = i === j ? valuesPerStatus[j] : null;
            }
            datasets.push({
                label: this.statusLabel(order[i]),
                data: data,
                backgroundColor: fill,
                borderColor: border,
                borderWidth: 2,
                categoryPercentage: 0.55,
                barPercentage: 0.72,
            });
        }

        this.destroyChart();

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;
        const self = this;
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();

        const yAxisMax = this.computeYAxisMax(valuesPerStatus);
        const statusOrder = this.statusAxisOrder;
        const statusValues = valuesPerStatus;

        this.chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: labels,
                datasets: datasets,
            },
            plugins: [
                createMonitorDashboardLegendHeightPlugin(10),
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawZeroValueMarkers(chart);
                        self.drawValueLabels(chart);
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                onClick: function (evt: any, elements: any[]) {
                    if (!elements || elements.length === 0) {
                        return;
                    }
                    const idx = elements[0]._index;
                    const count =
                        statusValues[idx] != null ? Number(statusValues[idx]) : 0;
                    if (isNaN(count) || count <= 0) {
                        return;
                    }
                    const statusId =
                        statusOrder[idx] != null ? Number(statusOrder[idx]) : 0;
                    if (statusId <= 0) {
                        return;
                    }
                    self.navigateToDetails(statusId);
                },
                legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            gridLines: { display: false },
                            ticks: buildMonitorDashboardCategoryAxisTicks(12, fontFamily),
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            ticks: buildMonitorDashboardValueAxisTicks(11, fontFamily, true, yAxisMax, undefined),
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    callbacks: {
                        title: function (items: any[]) {
                            if (!items || items.length === 0) return "";
                            return labels[items[0].index];
                        },
                        label: function (tooltipItem: any, data: any) {
                            const ds = data && data.datasets && data.datasets[tooltipItem.datasetIndex]
                                ? data.datasets[tooltipItem.datasetIndex]
                                : null;
                            const raw = ds && ds.data ? Number(ds.data[tooltipItem.index]) : 0;
                            const value = isNaN(raw) ? 0 : raw;
                            return (
                                String(self.$t("monitorDashboard.complaintsStatusesTooltipCount")) +
                                ": " +
                                String(value)
                            );
                        },
                    },
                }),
            }),
        });
    }

    private drawValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "bottom";
        ctx2.font = "11px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#374151";

        for (let di = 0; di < chart.data.datasets.length; di++) {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) continue;

            for (let i = 0; i < meta.data.length; i++) {
                const raw = chart.data.datasets[di].data[i];
                if (raw === null || raw === undefined) continue;

                const bar = meta.data[i];
                const val = Number(raw);
                let y = bar._model.y - 6;
                if (val === 0) {
                    const yZero = chart.scales["y-axis-0"].getPixelForValue(0);
                    y = yZero - 6;
                }
                ctx2.fillText(String(val), bar._model.x, y);
            }
        }

        ctx2.restore();
    }

    private drawZeroValueMarkers(chart: any): void {
        const ctx2: any = chart.ctx;
        const yAxis = chart.scales["y-axis-0"];
        if (!yAxis) return;

        const yZero = yAxis.getPixelForValue(0);

        ctx2.save();
        ctx2.lineWidth = 3;

        for (let di = 0; di < chart.data.datasets.length; di++) {
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.hidden) continue;

            const strokeColor = chart.data.datasets[di].borderColor;

            for (let i = 0; i < meta.data.length; i++) {
                const raw = chart.data.datasets[di].data[i];
                if (raw === null || raw === undefined) continue;
                const val = Number(raw);
                if (val !== 0) continue;

                const bar = meta.data[i];
                const model = bar._model;
                const x = model.x;
                const halfWidth = Math.max(1, (model.width || 0) / 2);

                ctx2.strokeStyle = strokeColor;
                ctx2.beginPath();
                ctx2.moveTo(x - halfWidth, yZero);
                ctx2.lineTo(x + halfWidth, yZero);
                ctx2.stroke();
            }
        }

        ctx2.restore();
    }

    private computeYAxisMax(values: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i] || 0);
            if (v > maxVal) maxVal = v;
        }
        if (maxVal <= 0) return 10;

        const withPadding = Math.ceil(maxVal * 1.05);
        const step = this.getNiceStep(Math.ceil(withPadding / 18));
        return Math.ceil(withPadding / step) * step;
    }

    private getNiceStep(rawStep: number): number {
        if (rawStep <= 1) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalized = rawStep / magnitude;
        if (normalized <= 1) return 1 * magnitude;
        if (normalized <= 2) return 2 * magnitude;
        if (normalized <= 5) return 5 * magnitude;
        return 10 * magnitude;
    }
}
